import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Laptop,
  Users as UsersIcon,
  CheckCircle,
  Circle,
  PlayCircle,
  XCircle,
  Edit,
  Trash2,
  Save,
} from 'lucide-react';
import api from '../services/api';
import type { SessionDetail, SessionStatus } from '../../../shared/session-types';

const statusConfig = {
  pendiente: { label: 'Pendiente', icon: Circle, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  en_curso: { label: 'En Curso', icon: PlayCircle, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  finalizada: { label: 'Finalizada', icon: CheckCircle, color: 'text-green-400', bgColor: 'bg-green-500/20' },
  cancelada: { label: 'Cancelada', icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/20' },
};

const gameStatusConfig = {
  pendiente: { label: 'Pendiente', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10' },
  en_curso: { label: 'En Curso', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  completado: { label: 'Completado', color: 'text-green-400', bgColor: 'bg-green-500/10' },
};

export default function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedStatus, setEditedStatus] = useState<SessionStatus>('pendiente');
  const [observacionesFinales, setObservacionesFinales] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadSession();
    }
  }, [id]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/professional/sessions/${id}`);

      if (response.data.ok) {
        setSession(response.data.data);
        setEditedStatus(response.data.data.estado);
        setObservacionesFinales(response.data.data.observaciones_finales || '');
      }
    } catch (error) {
      console.error('Error al cargar sesión:', error);
      alert('Error al cargar la sesión');
      navigate('/profesional/sesiones');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!session) return;

    try {
      setSaving(true);
      const response = await api.put(`/professional/sessions/${session.id}`, {
        estado: editedStatus,
        observaciones_finales: observacionesFinales || undefined,
      });

      if (response.data.ok) {
        alert('✅ Sesión actualizada correctamente');
        setEditing(false);
        loadSession();
      }
    } catch (error: any) {
      console.error('Error al actualizar sesión:', error);
      alert(error.response?.data?.error || 'Error al actualizar sesión');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!session) return;

    if (
      !confirm(
        `¿Eliminar sesión para ${session.paciente_nombre} ${session.paciente_apellidos}?\n\nEsta acción NO se puede deshacer.`
      )
    ) {
      return;
    }

    try {
      const response = await api.delete(`/professional/sessions/${session.id}`);
      if (response.data.ok) {
        alert('✅ Sesión eliminada');
        navigate('/profesional/sesiones');
      }
    } catch (error: any) {
      console.error('Error al eliminar sesión:', error);
      alert(error.response?.data?.error || 'Error al eliminar sesión');
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
          <p className="mt-4 text-white/70">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70">Sesión no encontrada</p>
          <Link to="/profesional/sesiones" className="btn-neureka mt-4">
            Volver a Sesiones
          </Link>
        </div>
      </div>
    );
  }

  const config = statusConfig[session.estado];

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/profesional/sesiones')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Sesiones
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-display font-bold mb-2">Detalle de Sesión</h1>
              <p className="text-white/70">
                Sesión #{session.id} - {session.paciente_nombre} {session.paciente_apellidos}
              </p>
            </div>
            <div className="flex gap-2">
              {!editing ? (
                <>
                  <button
                    onClick={() => setEditing(true)}
                    className="btn-neureka-secondary"
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-3 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditedStatus(session.estado);
                      setObservacionesFinales(session.observaciones_finales || '');
                    }}
                    className="btn-neureka-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn-neureka"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Guardar
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Info Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Estado y Datos Básicos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-container"
            >
              <h2 className="text-xl font-bold mb-4">Información General</h2>

              <div className="space-y-4">
                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium mb-2">Estado</label>
                  {editing ? (
                    <select
                      value={editedStatus}
                      onChange={(e) => setEditedStatus(e.target.value as SessionStatus)}
                      className="input-neureka w-full"
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="en_curso">En Curso</option>
                      <option value="finalizada">Finalizada</option>
                      <option value="cancelada">Cancelada</option>
                    </select>
                  ) : (
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${config.bgColor}`}>
                      <config.icon className={`w-5 h-5 ${config.color}`} />
                      <span className={`font-medium ${config.color}`}>{config.label}</span>
                    </div>
                  )}
                </div>

                {/* Tipo */}
                <div className="flex items-center gap-2 text-white/70">
                  {session.tipo_sesion === 'remota' ? (
                    <>
                      <Laptop className="w-5 h-5" />
                      <span>Sesión Remota</span>
                    </>
                  ) : (
                    <>
                      <UsersIcon className="w-5 h-5" />
                      <span>Sesión Presencial</span>
                    </>
                  )}
                </div>

                {/* Fechas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-white/50 mb-1">Creada</div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(session.fecha_creacion)}
                    </div>
                  </div>
                  {session.fecha_inicio && (
                    <div>
                      <div className="text-white/50 mb-1">Iniciada</div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {formatDate(session.fecha_inicio)}
                      </div>
                    </div>
                  )}
                  {session.fecha_finalizacion && (
                    <div>
                      <div className="text-white/50 mb-1">Finalizada</div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        {formatDate(session.fecha_finalizacion)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Objetivos */}
                {session.objetivos && (
                  <div>
                    <div className="text-white/50 text-sm mb-2">Objetivos</div>
                    <div className="p-3 rounded-lg bg-white/5">
                      {session.objetivos}
                    </div>
                  </div>
                )}

                {/* Observaciones Iniciales */}
                {session.observaciones_iniciales && (
                  <div>
                    <div className="text-white/50 text-sm mb-2">Observaciones Iniciales</div>
                    <div className="p-3 rounded-lg bg-white/5">
                      {session.observaciones_iniciales}
                    </div>
                  </div>
                )}

                {/* Observaciones Finales */}
                <div>
                  <div className="text-white/50 text-sm mb-2">Observaciones Finales</div>
                  {editing ? (
                    <textarea
                      value={observacionesFinales}
                      onChange={(e) => setObservacionesFinales(e.target.value)}
                      placeholder="Notas y conclusiones tras finalizar la sesión..."
                      className="input-neureka w-full h-24 resize-none"
                    />
                  ) : session.observaciones_finales ? (
                    <div className="p-3 rounded-lg bg-white/5">
                      {session.observaciones_finales}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-white/5 text-white/50 italic">
                      Sin observaciones finales
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Juegos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-container"
            >
              <h2 className="text-xl font-bold mb-4">Juegos Asignados</h2>

              <div className="space-y-3">
                {session.juegos.map((juego, index) => {
                  const gameConfig = gameStatusConfig[juego.estado];
                  return (
                    <div
                      key={juego.id}
                      className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-neureka-yellow/20 flex items-center justify-center text-sm font-bold">
                            {juego.orden}
                          </div>
                          <div>
                            <h3 className="font-semibold">{juego.juego_nombre}</h3>
                            <p className="text-sm text-white/60">
                              Dificultad: {juego.dificultad_nombre}
                            </p>
                          </div>
                        </div>
                        <div className={`px-3 py-1 rounded-lg ${gameConfig.bgColor}`}>
                          <span className={`text-xs font-medium ${gameConfig.color}`}>
                            {gameConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Paciente */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-container"
            >
              <h3 className="font-semibold mb-3">Paciente</h3>
              <div className="flex items-center gap-3">
                {session.paciente_avatar ? (
                  <img
                    src={session.paciente_avatar}
                    alt={session.paciente_nombre}
                    className="w-12 h-12 rounded-full"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-300" />
                  </div>
                )}
                <div>
                  <div className="font-semibold">
                    {session.paciente_nombre} {session.paciente_apellidos}
                  </div>
                  <div className="text-sm text-white/50">@{session.paciente_username}</div>
                </div>
              </div>
            </motion.div>

            {/* Estadísticas */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-container"
            >
              <h3 className="font-semibold mb-4">Progreso</h3>

              <div className="space-y-4">
                {/* Barra de Progreso */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-white/70">Completado</span>
                    <span className="font-semibold">{session.stats.progreso_porcentaje}%</span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-neureka-yellow rounded-full transition-all"
                      style={{ width: `${session.stats.progreso_porcentaje}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="text-white/50 mb-1">Total</div>
                    <div className="text-2xl font-bold">{session.stats.total_juegos}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-green-500/10">
                    <div className="text-green-400/70 mb-1">Completados</div>
                    <div className="text-2xl font-bold text-green-400">
                      {session.stats.juegos_completados}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10">
                    <div className="text-blue-400/70 mb-1">En Curso</div>
                    <div className="text-2xl font-bold text-blue-400">
                      {session.stats.juegos_en_curso}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-yellow-500/10">
                    <div className="text-yellow-400/70 mb-1">Pendientes</div>
                    <div className="text-2xl font-bold text-yellow-400">
                      {session.stats.juegos_pendientes}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
